import { createFileRoute } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import {
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { Button } from '@apps/web/components/ui/button';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import { cn } from '@apps/web/lib/utils';
import {
    getSimulatorWorldDisplayLive,
    type SimulatorWorldDisplayLiveResponse,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { ApiError } from '@libraries/typescript/api_client';

export const Route = createFileRoute('/simulator-world/display')({
    component: SimulatorWorldDisplayPage,
    head: () => ({
        meta: [
            {
                title: 'Simulator World Display',
            },
        ],
    }),
});

const DISPLAY_REFRESH_INTERVAL_MS = 15_000;
const DISPLAY_TOKEN_STORAGE_KEY = 'phoebe-simulator-world-display-token';

type SimReadyMessage = {
    readonly type: 'sim-world:ready';
};

function normalizeSimWorldUrl(simWorldUrl: string): string {
    return simWorldUrl.endsWith('/') ? simWorldUrl : `${simWorldUrl}/`;
}

function displayErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message) {
        return error.message;
    }
    return 'Unable to load Simulator World display.';
}

function isInvalidDisplayTokenError(error: unknown): boolean {
    return error instanceof ApiError && error.status === 401;
}

function isDisplayStoppingError(error: unknown): boolean {
    return (
        error instanceof ApiError &&
        (error.status === 401 || error.status === 404)
    );
}

function useDisplayToken() {
    const [token, setToken] = useState<string | null>(null);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const hashToken = window.location.hash.startsWith('#')
            ? window.location.hash.slice(1).trim()
            : '';
        const queryToken = searchParams.get('display_token')?.trim() ?? '';
        if (hashToken) {
            searchParams.delete('display_token');
            const sanitizedSearch = searchParams.toString();
            localStorage.setItem(DISPLAY_TOKEN_STORAGE_KEY, hashToken);
            window.history.replaceState(
                null,
                '',
                `${window.location.pathname}${
                    sanitizedSearch ? `?${sanitizedSearch}` : ''
                }`,
            );
            setToken(hashToken);
            setInitialized(true);
            return;
        }

        if (queryToken) {
            localStorage.setItem(DISPLAY_TOKEN_STORAGE_KEY, queryToken);
            setToken(queryToken);
            setInitialized(true);
            return;
        }

        const storedToken = localStorage.getItem(DISPLAY_TOKEN_STORAGE_KEY);
        setToken(storedToken?.trim() || null);
        setInitialized(true);
    }, []);

    return { initialized, token };
}

function SimulatorWorldDisplayPage() {
    const simWorldUrl = getRuntimeConfig().simulatorWorldUrl;
    const { initialized, token } = useDisplayToken();
    const [liveData, setLiveData] =
        useState<SimulatorWorldDisplayLiveResponse | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [simReady, setSimReady] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const liveDataRef = useRef<SimulatorWorldDisplayLiveResponse | null>(null);
    const requestSequenceRef = useRef(0);
    const lastSuccessSequenceRef = useRef(0);

    const simWorldOrigin = useMemo(
        () =>
            simWorldUrl
                ? new URL(simWorldUrl, window.location.origin).origin
                : null,
        [simWorldUrl],
    );
    const simWorldIframeUrl = simWorldUrl
        ? `${normalizeSimWorldUrl(simWorldUrl)}index.html?embed=1&fit=cover`
        : null;

    const postToSim = useCallback(
        (message: object) => {
            if (!simWorldOrigin) {
                return;
            }
            iframeRef.current?.contentWindow?.postMessage(
                message,
                simWorldOrigin,
            );
        },
        [simWorldOrigin],
    );

    useEffect(() => {
        if (!initialized) {
            return;
        }
        if (!simWorldIframeUrl) {
            setLoading(false);
            setSimReady(false);
            setLiveData(null);
            liveDataRef.current = null;
            lastSuccessSequenceRef.current = 0;
            return;
        }
        let cancelled = false;
        const displayToken = token;

        async function loadDisplay() {
            const requestSequence = requestSequenceRef.current + 1;
            requestSequenceRef.current = requestSequence;
            try {
                const response =
                    await getSimulatorWorldDisplayLive(displayToken);
                if (
                    cancelled ||
                    requestSequence < lastSuccessSequenceRef.current
                ) {
                    return;
                }
                lastSuccessSequenceRef.current = requestSequence;
                liveDataRef.current = response;
                setLiveData(response);
                setErrorMessage(null);
            } catch (error) {
                if (
                    cancelled ||
                    requestSequence < lastSuccessSequenceRef.current
                ) {
                    return;
                }
                if (isDisplayStoppingError(error)) {
                    if (
                        displayToken !== null &&
                        isInvalidDisplayTokenError(error)
                    ) {
                        localStorage.removeItem(DISPLAY_TOKEN_STORAGE_KEY);
                    }
                    liveDataRef.current = null;
                    lastSuccessSequenceRef.current = 0;
                    setLiveData(null);
                    setSimReady(false);
                    setErrorMessage(displayErrorMessage(error));
                    setLoading(false);
                    window.clearInterval(intervalId);
                    cancelled = true;
                    return;
                }
                if (liveDataRef.current === null) {
                    setLiveData(null);
                    setSimReady(false);
                    setErrorMessage(displayErrorMessage(error));
                    return;
                }
            } finally {
                if (
                    !cancelled &&
                    requestSequence >= lastSuccessSequenceRef.current
                ) {
                    setLoading(false);
                }
            }
        }

        const intervalId = window.setInterval(
            () => void loadDisplay(),
            DISPLAY_REFRESH_INTERVAL_MS,
        );
        setLoading(true);
        void loadDisplay();
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [initialized, simWorldIframeUrl, token]);

    useEffect(() => {
        function onMessage(event: MessageEvent) {
            if (!simWorldOrigin || event.origin !== simWorldOrigin) {
                return;
            }
            const message = event.data as SimReadyMessage | undefined;
            if (
                message &&
                typeof message === 'object' &&
                message.type === 'sim-world:ready'
            ) {
                setSimReady(true);
            }
        }
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [simWorldOrigin]);

    useEffect(() => {
        if (!simReady || liveData === null) {
            return;
        }
        postToSim({
            type: 'sim-world:live',
            live: liveData,
        });
    }, [liveData, postToSim, simReady]);

    if (!initialized) {
        return (
            <DisplayState title="Loading Simulator World">
                Checking display link.
            </DisplayState>
        );
    }

    if (!simWorldIframeUrl) {
        return (
            <DisplayState title="Simulator World unavailable">
                Simulator World is not configured for this environment.
            </DisplayState>
        );
    }

    if (errorMessage !== null) {
        return (
            <DisplayState title="Display link unavailable">
                {errorMessage}
            </DisplayState>
        );
    }

    return (
        <div className="h-screen w-screen overflow-hidden bg-[#0b1020]">
            <iframe
                ref={iframeRef}
                src={simWorldIframeUrl}
                title="Simulator World"
                className="h-full w-full border-0"
            />
            <div
                className={cn(
                    'pointer-events-none fixed inset-x-0 top-0 flex justify-center p-4 transition-opacity',
                    loading || !simReady ? 'opacity-100' : 'opacity-0',
                )}
            >
                <div className="inline-flex items-center gap-2 rounded-md bg-black/70 px-3 py-2 text-sm text-white">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading Simulator World
                </div>
            </div>
        </div>
    );
}

function DisplayState({
    children,
    title,
}: {
    readonly children: ReactNode;
    readonly title: string;
}) {
    return (
        <div className="flex min-h-screen items-center justify-center bg-[#0b1020] p-6 text-white">
            <div className="max-w-md space-y-4 text-center">
                <h1 className="text-2xl font-semibold">{title}</h1>
                <p className="text-sm text-white/75">{children}</p>
                <Button
                    type="button"
                    variant="secondary"
                    onClick={() => window.location.reload()}
                >
                    Retry
                </Button>
            </div>
        </div>
    );
}
